import { compareDesc, parseISO } from 'date-fns';

import type { CrossUserDetailDto } from '@/libs/api-user-mgt/generated/model';
import type { CrossUserListItemDto } from '@/libs/api-user-mgt/generated/model';
import type { UserDatabaseDetailEntryDto } from '@/libs/api-user-mgt/generated/model';

import type { UserWireDto } from '@/userManagement/application/types/UserWireDto';
import type { UserDatabaseRegistration, TenantDatabaseEntry } from '@/userManagement/domain/entities/User';

import { isNestedUsernameDataBundle } from './userListBundleGuards';
import { dedupeTenantDatabaseEntries, parseTenantDatabaseEntry } from '@/userManagement/application/utils/parseTenantDatabaseEntry';
import { joinDistinctCustomIds, parseWireCustomId } from '@/userManagement/application/utils/parseWireCustomId';

export function parseInProgressApprovalRequestCount(record: Record<string, unknown>): number | undefined {
  const raw = record.inProgressApprovalRequestCount ?? record.InProgressApprovalRequestCount;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return Math.floor(raw);
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

export function parseApprovedApprovalRequestCount(record: Record<string, unknown>): number | undefined {
  const raw = record.approvedApprovalRequestCount ?? record.ApprovedApprovalRequestCount;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return Math.floor(raw);
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

/** TROC-301 — parse nullable pending requested role id from list/detail wire. */
export function parsePendingNewRoleId(record: Record<string, unknown>): number | null | undefined {
  if (!('pendingNewRoleId' in record) && !('PendingNewRoleId' in record)) {
    return undefined;
  }
  const raw = record.pendingNewRoleId ?? record.PendingNewRoleId;
  if (raw === null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.floor(raw);
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** TROC-301 — parse nullable pending requested role name from list/detail wire. */
export function parsePendingNewRoleName(record: Record<string, unknown>): string | null | undefined {
  if (!('pendingNewRoleName' in record) && !('PendingNewRoleName' in record)) {
    return undefined;
  }
  const raw = record.pendingNewRoleName ?? record.PendingNewRoleName;
  if (raw === null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t === '' ? null : t;
  }
  return null;
}

function pendingRoleFieldsFromRecord(record: Record<string, unknown>): {
  pendingNewRoleId?: number | null;
  pendingNewRoleName?: string | null;
} {
  const pendingNewRoleId = parsePendingNewRoleId(record);
  const pendingNewRoleName = parsePendingNewRoleName(record);
  const hasPending =
    (pendingNewRoleId !== undefined && pendingNewRoleId !== null) ||
    (pendingNewRoleName !== undefined && pendingNewRoleName !== null && pendingNewRoleName !== '');
  if (!hasPending && pendingNewRoleId === undefined && pendingNewRoleName === undefined) {
    return {};
  }
  if (!hasPending) {
    return {};
  }
  return {
    ...(pendingNewRoleId !== undefined ? { pendingNewRoleId } : {}),
    ...(pendingNewRoleName !== undefined ? { pendingNewRoleName } : {})
  };
}

/**
 * True when `row` matches OpenAPI {@link CrossUserDetailDto} (GET /users/{username}): no top-level cross-user id; shard rows carry `id`.
 */
export function isCrossUserDetailShape(row: unknown): row is CrossUserDetailDto {
  if (row === null || typeof row !== 'object') return false;
  if (isNestedUsernameDataBundle(row)) return false;
  const o = row as Record<string, unknown>;
  if (typeof o.username !== 'string' || o.username.trim() === '') return false;
  const topId = o.id ?? o.crossUserId;
  if (topId != null && String(topId).trim() !== '') return false;
  const db = o.databases;
  if (!Array.isArray(db) || db.length === 0) return false;
  const first = db[0];
  if (first === null || typeof first !== 'object' || Array.isArray(first)) return false;
  const f = first as Record<string, unknown>;
  return f.id != null && String(f.id).trim() !== '';
}

/**
 * True when `row` matches OpenAPI {@link CrossUserListItemDto} (paged list), not a legacy per-shard row or {@link CrossUserDetailDto}.
 */
export function isCrossUserListItemShape(row: unknown): boolean {
  if (row === null || typeof row !== 'object') return false;
  if (isNestedUsernameDataBundle(row)) return false;
  if (isCrossUserDetailShape(row)) return false;
  const o = row as Record<string, unknown>;
  if (typeof o.username !== 'string' || o.username.trim() === '') return false;
  const idRaw = o.id ?? o.crossUserId;
  if (idRaw == null || String(idRaw).trim() === '') return false;

  if (o.primaryPhone != null || o.primaryEmail != null || Array.isArray(o.primaryPhones) || Array.isArray(o.primaryEmails)) {
    return true;
  }

  const db = o.databases;
  if (!Array.isArray(db) || db.length === 0) {
    return false;
  }
  const first = db[0];
  if (first === null || typeof first !== 'object' || Array.isArray(first)) {
    return false;
  }
  return 'tenantDatabaseName' in first || 'databaseId' in first;
}

function parseFailedAttempts(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function parseIsoDate(value: string | null | undefined): Date | null {
  if (value == null || String(value).trim() === '') return null;
  try {
    const d = parseISO(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/** Maps `CrossUserDetailDto` (GET user by username) to a single aggregated {@link UserWireDto}. */
export function mapCrossUserDetailToWireDto(detail: CrossUserDetailDto | unknown): UserWireDto | null {
  if (detail === null || typeof detail !== 'object') return null;
  const dto = detail as CrossUserDetailDto;
  // Cast to wider record to read fields the API returns but the generated TS type doesn't declare
  const raw = detail as Record<string, unknown>;
  const username = typeof dto.username === 'string' ? dto.username.trim() : '';
  if (username === '') return null;
  const shards = (dto.databases ?? []) as UserDatabaseDetailEntryDto[];
  if (shards.length === 0) return null;

  const idRaw = shards[0]?.id;
  if (idRaw == null || String(idRaw).trim() === '') return null;

  // Read firstName/lastName from top-level response (may exist as undeclared API fields)
  const firstNameTopRaw = raw.firstName ?? raw.first_name ?? raw.FirstName;
  let firstName = typeof firstNameTopRaw === 'string' ? firstNameTopRaw.trim() : '';
  const lastNameTopRaw = raw.lastName ?? raw.last_name ?? raw.LastName;
  let lastName = typeof lastNameTopRaw === 'string' ? lastNameTopRaw.trim() : '';

  // Read permissionRoleId from top-level response (may also be undeclared)
  const roleIdTopRaw = raw.permissionRoleId ?? raw.permission_role_id ?? raw.roleId;
  let permissionRoleIdResolved: number | null = null;
  if (typeof roleIdTopRaw === 'number' && Number.isFinite(roleIdTopRaw)) {
    permissionRoleIdResolved = roleIdTopRaw;
  } else if (roleIdTopRaw != null && roleIdTopRaw !== '') {
    const n = parseInt(String(roleIdTopRaw), 10);
    permissionRoleIdResolved = Number.isNaN(n) ? null : n;
  }

  // Read email/phone/roleName from top-level response, falling back to shard aggregation below
  const emailTopRaw = raw.email ?? raw.primaryEmail ?? raw.primary_email ?? raw.Email;
  let emailFromTop = typeof emailTopRaw === 'string' ? emailTopRaw.trim() : '';
  const phoneTopRaw = raw.phone ?? raw.primaryPhone ?? raw.primary_phone ?? raw.Phone;
  let phoneFromTop = typeof phoneTopRaw === 'string' ? phoneTopRaw.trim() : null;
  const roleNameTopRaw = raw.roleName ?? raw.role_name ?? raw.permissionRoleName ?? raw.RoleName;
  let roleNameFromTop = typeof roleNameTopRaw === 'string' ? roleNameTopRaw.trim() : '';

  const assignedDatabases: string[] = [];
  const tenantDatabaseEntries: TenantDatabaseEntry[] = [];
  const seenDb = new Set<string>();
  for (const s of shards) {
    const parsed = parseTenantDatabaseEntry(s);
    if (!parsed) continue;
    const n = parsed.tenantDatabaseName;
    const k = n.toLowerCase();
    if (seenDb.has(k)) continue;
    seenDb.add(k);
    assignedDatabases.push(n);
    tenantDatabaseEntries.push(parsed);
  }

  const phonesSeen = new Set<string>();
  const phones: string[] = [];
  const emailsSeen = new Set<string>();
  const emails: string[] = [];
  const roleNamesSeen = new Set<string>();
  const roleNames: string[] = [];

  for (const s of shards) {
    // Widen the shard type to read undeclared fields that may be present in the actual API response
    const sr = s as unknown as Record<string, unknown>;

    const p = s.primaryPhone != null ? String(s.primaryPhone).trim() : '';
    if (p !== '') {
      const pk = p.toLowerCase();
      if (!phonesSeen.has(pk)) {
        phonesSeen.add(pk);
        phones.push(p);
      }
    }
    const e = s.primaryEmail != null ? String(s.primaryEmail).trim() : '';
    if (e !== '') {
      const ek = e.toLowerCase();
      if (!emailsSeen.has(ek)) {
        emailsSeen.add(ek);
        emails.push(e);
      }
    }
    const rn = s.roleName != null ? String(s.roleName).trim() : '';
    if (rn !== '') {
      const rk = rn.toLowerCase();
      if (!roleNamesSeen.has(rk)) {
        roleNamesSeen.add(rk);
        roleNames.push(rn);
      }
    }

    // Fall back to shard-level firstName/lastName if not found at top level
    if (firstName === '') {
      const v = sr.firstName ?? sr.first_name ?? sr.FirstName;
      if (typeof v === 'string' && v.trim() !== '') firstName = v.trim();
    }
    if (lastName === '') {
      const v = sr.lastName ?? sr.last_name ?? sr.LastName;
      if (typeof v === 'string' && v.trim() !== '') lastName = v.trim();
    }

    // Fall back to shard-level email/phone/roleName if not found at top level
    if (emailFromTop === '') {
      const v = sr.email ?? sr.primaryEmail ?? sr.primary_email;
      if (typeof v === 'string' && v.trim() !== '') emailFromTop = v.trim();
    }
    if (phoneFromTop === null) {
      const v = sr.phone ?? sr.primaryPhone ?? sr.primary_phone;
      if (typeof v === 'string' && v.trim() !== '') phoneFromTop = v.trim();
    }
    if (roleNameFromTop === '') {
      const v = sr.roleName ?? sr.role_name ?? sr.permissionRoleName;
      if (typeof v === 'string' && v.trim() !== '') roleNameFromTop = v.trim();
    }

    // Fall back to shard-level permissionRoleId if not found at top level
    if (permissionRoleIdResolved === null) {
      const rIdRaw = sr.permissionRoleId ?? sr.permission_role_id ?? sr.roleId;
      if (typeof rIdRaw === 'number' && Number.isFinite(rIdRaw)) {
        permissionRoleIdResolved = rIdRaw;
      } else if (rIdRaw != null && rIdRaw !== '') {
        const n = parseInt(String(rIdRaw), 10);
        if (!Number.isNaN(n)) permissionRoleIdResolved = n;
      }
    }
  }

  const phoneJoined = phones.length > 0 ? phones.join(', ') : null;
  const emailJoined = emails.join(', ');
  const permissionRoleNameJoined = roleNames.length === 0 ? null : roleNames.length === 1 ? roleNames[0]! : roleNames.join(', ');

  let failedSum = 0;
  for (const s of shards) {
    failedSum += parseFailedAttempts(s.failedAttempts);
  }

  let lastLoginMax: string | null = null;
  let lastLoginMaxDate: Date | null = null;
  for (const s of shards) {
    const parsed = parseIsoDate(s.lastLoginOn);
    if (!parsed) continue;
    if (!lastLoginMaxDate || compareDesc(parsed, lastLoginMaxDate) < 0) {
      lastLoginMaxDate = parsed;
      lastLoginMax = s.lastLoginOn != null ? String(s.lastLoginOn) : null;
    }
  }

  const isLocked = shards.every((s) => Boolean(s.isLocked));
  const isSuspended = shards.every((s) => Boolean(s.isSuspended));
  // isSystemAdmin is returned by the API but not yet declared on UserDatabaseDetailEntryDto
  const isSystemAdminFromTop = Boolean(raw.isSystemAdmin ?? raw.is_system_admin);
  const isSystemAdmin =
    isSystemAdminFromTop ||
    shards.some((s) => {
      const sr = s as unknown as Record<string, unknown>;
      return Boolean(sr.isSystemAdmin ?? sr.is_system_admin);
    });
  const isActive = shards.every((s) => (s.isActive === undefined ? true : Boolean(s.isActive)));
  const forceNewPassword = shards.every((s) => Boolean(s.forceNewPassword));

  const databaseRegistrations: UserDatabaseRegistration[] = [];
  for (const s of shards) {
    const crossUserId = s.id != null ? String(s.id).trim() : '';
    const tenantDatabaseName = s.tenantDatabaseName != null ? String(s.tenantDatabaseName).trim() : '';
    if (crossUserId === '' || tenantDatabaseName === '') continue;
    const databaseIdRaw = s.databaseId != null ? String(s.databaseId).trim() : '';
    const shardRecord = s as unknown as Record<string, unknown>;
    const databaseAliasRaw = shardRecord.databaseAlias ?? shardRecord.dbAlias ?? shardRecord.alias;
    const databaseAlias = typeof databaseAliasRaw === 'string' && databaseAliasRaw.trim() !== '' ? databaseAliasRaw.trim() : null;
    const shardCustomId = parseWireCustomId(shardRecord);
    databaseRegistrations.push({
      crossUserId,
      tenantDatabaseName,
      databaseAlias,
      ...(databaseIdRaw !== '' ? { databaseId: databaseIdRaw } : {}),
      ...(shardCustomId != null ? { customId: shardCustomId } : {})
    });
  }

  const customIdFromTop = parseWireCustomId(raw);
  const customId = customIdFromTop ?? joinDistinctCustomIds(databaseRegistrations.map((r) => r.customId));

  // TROC-301: first shard with an in-flight pending role (detail is per-DB; UI shows one pending line).
  let pendingFromShards: { pendingNewRoleId?: number | null; pendingNewRoleName?: string | null } = {};
  for (const s of shards) {
    const fields = pendingRoleFieldsFromRecord(s as unknown as Record<string, unknown>);
    if (fields.pendingNewRoleId !== undefined || fields.pendingNewRoleName !== undefined) {
      pendingFromShards = fields;
      break;
    }
  }

  return {
    id: String(idRaw).trim(),
    username,
    firstName,
    lastName,
    email: emailFromTop !== '' ? emailFromTop : emailJoined,
    phone: phoneFromTop !== null ? phoneFromTop : phoneJoined,
    customId,
    permissionRoleId: permissionRoleIdResolved,
    permissionRoleName: roleNameFromTop !== '' ? roleNameFromTop : permissionRoleNameJoined,
    permissionRoleNames: roleNames.length > 0 ? roleNames : undefined,
    isLocked,
    isSuspended,
    isSystemAdmin,
    isActive,
    forceNewPassword,
    failedLoginAttempts: failedSum,
    lastLoginOn: lastLoginMax,
    assignedDatabases,
    tenantDatabaseEntries: tenantDatabaseEntries.length > 0 ? tenantDatabaseEntries : undefined,
    utcUpdatedOn: new Date().toISOString(),
    databaseRegistrations: databaseRegistrations.length > 0 ? databaseRegistrations : undefined,
    ...pendingFromShards
  };
}

/** Maps `CrossUserListItemDto` (paged users list) to {@link UserWireDto}. */
export function mapCrossUserListItemToWireDto(item: CrossUserListItemDto | unknown): UserWireDto | null {
  if (item === null || typeof item !== 'object') return null;
  const dto = item as CrossUserListItemDto & Record<string, unknown>;
  const idRaw = dto.id;
  if (idRaw == null || String(idRaw).trim() === '') return null;
  const username = typeof dto.username === 'string' ? dto.username.trim() : '';
  if (username === '') return null;

  const listDbEntries = Array.isArray(dto.databases)
    ? dedupeTenantDatabaseEntries(dto.databases.map(parseTenantDatabaseEntry).filter((e): e is TenantDatabaseEntry => e != null))
    : [];
  const assignedDatabases = listDbEntries.map((e) => e.tenantDatabaseName);

  const phoneSingle = dto.primaryPhone != null ? String(dto.primaryPhone).trim() : '';
  let phone: string | null =
    phoneSingle !== ''
      ? phoneSingle
      : Array.isArray(dto.primaryPhones)
        ? ((dto.primaryPhones as unknown[]).map((p) => String(p).trim()).find((p) => p !== '') ?? null)
        : null;

  const emailSingle = dto.primaryEmail != null ? String(dto.primaryEmail).trim() : '';
  let email: string =
    emailSingle !== ''
      ? emailSingle
      : Array.isArray(dto.primaryEmails)
        ? ((dto.primaryEmails as unknown[]).map((e) => String(e).trim()).find((e) => e !== '') ?? '')
        : '';

  // The list endpoint may return null at top level even when shards (`databases[].primaryEmail`,
  // `databases[].primaryPhone`) hold values — aggregate the first non-empty shard value so the
  // grid row (and the contact-info seed it feeds into the details modal) stays populated.
  if (Array.isArray(dto.databases)) {
    for (const d of dto.databases as unknown[]) {
      if (d == null || typeof d !== 'object') continue;
      const sr = d as Record<string, unknown>;
      if (email === '') {
        const v = sr.primaryEmail ?? sr.email ?? sr.primary_email;
        if (typeof v === 'string' && v.trim() !== '') email = v.trim();
      }
      if (phone == null || phone === '') {
        const v = sr.primaryPhone ?? sr.phone ?? sr.primary_phone;
        if (typeof v === 'string' && v.trim() !== '') phone = v.trim();
      }
      if (email !== '' && phone != null && phone !== '') break;
    }
  }

  const roleIdRaw = dto.permissionRoleId ?? dto.permission_role_id ?? dto.roleId;
  let permissionRoleId: number | null = null;
  if (typeof roleIdRaw === 'number' && Number.isFinite(roleIdRaw)) {
    permissionRoleId = roleIdRaw;
  } else if (roleIdRaw != null && roleIdRaw !== '') {
    const n = parseInt(String(roleIdRaw), 10);
    permissionRoleId = Number.isNaN(n) ? null : n;
  }

  const firstNameRaw = dto.firstName ?? dto.first_name ?? '';
  const lastNameRaw = dto.lastName ?? dto.last_name ?? '';

  const pendingList = parseInProgressApprovalRequestCount(dto);
  const approvedList = parseApprovedApprovalRequestCount(dto);
  const pendingRole = pendingRoleFieldsFromRecord(dto);

  return {
    id: String(idRaw).trim(),
    username,
    firstName: typeof firstNameRaw === 'string' ? firstNameRaw.trim() : '',
    lastName: typeof lastNameRaw === 'string' ? lastNameRaw.trim() : '',
    email,
    phone: phone != null && phone !== '' ? phone : null,
    customId: parseWireCustomId(dto),
    permissionRoleId,
    permissionRoleName: dto.roleName != null && String(dto.roleName).trim() !== '' ? String(dto.roleName).trim() : null,
    isLocked: Boolean(dto.isLocked),
    isSuspended: Boolean(dto.isSuspended),
    isSystemAdmin: Boolean(dto.isSystemAdmin ?? dto.is_system_admin),
    isActive: dto.isActive !== undefined ? Boolean(dto.isActive) : true,
    forceNewPassword: Boolean(dto.forceNewPassword),
    failedLoginAttempts: parseFailedAttempts(dto.failedAttempts),
    lastLoginOn: dto.lastLoginOn != null ? String(dto.lastLoginOn) : null,
    assignedDatabases,
    tenantDatabaseEntries: listDbEntries.length > 0 ? listDbEntries : undefined,
    utcUpdatedOn:
      dto.utcUpdatedOn != null && String(dto.utcUpdatedOn).trim() !== '' ? String(dto.utcUpdatedOn).trim() : new Date().toISOString(),
    deviationsPreview: dto.deviationsPreview as UserWireDto['deviationsPreview'],
    ...(pendingList !== undefined ? { inProgressApprovalRequestCount: pendingList } : {}),
    ...(approvedList !== undefined ? { approvedApprovalRequestCount: approvedList } : {}),
    ...pendingRole
  };
}

/**
 * Maps GET user payloads: {@link CrossUserDetailDto} (by username) or legacy {@link CrossUserListItemDto} shape.
 */
export function mapCrossUserPayloadToWireDto(payload: unknown): UserWireDto | null {
  if (isCrossUserDetailShape(payload)) {
    return mapCrossUserDetailToWireDto(payload);
  }
  return mapCrossUserListItemToWireDto(payload);
}
